/**
 * Ted #23 (#984), 09-16 re-review — fold a day feed's SERVICE LEGS onto the
 * visits they belong to.
 *
 * A leg is a Booking, so `/today` and `/rollup` return it as its own row. On a
 * list that is really a list of BOOKINGS (the Bookings page) that is correct.
 * On the two surfaces that answer "who is coming in today" — the Front Desk
 * list and the PRINTED morning board — it is not: Milo arriving once with a
 * nail trim and a bath rendered as three rows, two of them saying "Grooming",
 * and the headline said "Expected today (3)". One dog read as three arrivals.
 * That is the same non-itemization Ted reported, on the busiest desk screen
 * and on the sheet that gets copied onto the whiteboard every morning.
 *
 * So: one entry per VISIT, carrying its added services. Two rules the fold
 * never breaks —
 *   1. NEVER DROP A ROW. A leg whose parent is not in this feed (the parent is
 *      a mid-stay boarder on `onSite`, or the day filter split them) becomes
 *      its own entry, tagged as an added service. A dog whose grooming is
 *      today must appear on today's board.
 *   2. Absence reads as "an ordinary booking". Frontends auto-deploy on merge
 *      and the server does not, so a payload from a server older than #984
 *      carries no `isLeg` at all — every row is then a visit, which is exactly
 *      the pre-#984 list, never a wrong grouping.
 */

/** The only fields the fold needs. `DashboardArrival` satisfies it. */
export interface ServiceLegRow {
  readonly bookingId: string;
  readonly serviceType: string;
  readonly serviceDisplayName?: string;
  /** True when this row IS an added service rather than a visit of its own. */
  readonly isLeg?: boolean;
  /** The visit this row hangs off, when it is an added service. */
  readonly parentBookingId?: string | null;
  /** The add-on's catalog name ("Nail Trim") — what the row should READ as. */
  readonly addOnName?: string | null;
}

export interface VisitWithLegs<T extends ServiceLegRow> {
  /** The visit row — or an orphan leg standing in for one (see rule 1). */
  readonly row: T;
  /** True when `row` is itself a leg with no parent in this feed. */
  readonly orphanLeg: boolean;
  /** The added services booked onto this visit, in feed order. */
  readonly legs: T[];
}

/**
 * What a row should READ as. For a leg, the add-on's own name — never the
 * service type it rides on, which is how two lines both came out "Grooming".
 */
export function serviceRowLabel(row: ServiceLegRow): string {
  if (row.isLeg === true) {
    const addOn = row.addOnName;
    if (typeof addOn === 'string' && addOn.trim().length > 0) return addOn;
  }
  return row.serviceDisplayName ?? row.serviceType;
}

export function foldServiceLegs<T extends ServiceLegRow>(
  rows: ReadonlyArray<T>,
): VisitWithLegs<T>[] {
  // Pass 1 over ALL rows first: a leg may precede its parent in the feed.
  const visitIds = new Set<string>();
  for (const row of rows) {
    if (row.isLeg !== true) visitIds.add(row.bookingId);
  }

  const entries: Array<{ row: T; orphanLeg: boolean; legs: T[] }> = [];
  const byVisit = new Map<string, { row: T; orphanLeg: boolean; legs: T[] }>();
  for (const row of rows) {
    const parentId = row.parentBookingId ?? null;
    const attachable =
      row.isLeg === true && parentId !== null && visitIds.has(parentId);
    if (attachable) continue;
    const entry = { row, orphanLeg: row.isLeg === true, legs: [] as T[] };
    entries.push(entry);
    if (row.isLeg !== true) byVisit.set(row.bookingId, entry);
  }
  for (const row of rows) {
    const parentId = row.parentBookingId ?? null;
    if (row.isLeg !== true || parentId === null) continue;
    byVisit.get(parentId)?.legs.push(row);
  }
  return entries;
}

/**
 * How many VISITS the desk should expect — not booking rows. The number the
 * Front Desk header and the morning board's "Expected today (N)" print.
 *
 * VISITS, said precisely (09-16, corrected after reading the screenshot this
 * round produced): a 2-dog boarding reservation is ONE entry here, so on a
 * feed of Rex+Bella boarding, Rufus, Kona and Rex's two add-ons this returns
 * 3, not 4. That is a strict improvement on what it replaced — `arrivals.length`
 * counted 5, pinning one dog as three arrivals — and it is NOT a dog count.
 * The round-2 comments on this function and on both headline call sites said
 * "dogs", which is the exact copy-vs-data fault #905 had to fix on the
 * household card ("Check in all 2 family dogs" while three dogs went in), so
 * the claim is corrected rather than left standing. Whether the desk wants
 * dogs or visits in that headline is Ted's call, tracked in #1029.
 */
export function countVisits(rows: ReadonlyArray<ServiceLegRow>): number {
  return foldServiceLegs(rows).length;
}
